import { ref, readonly } from 'vue'

export interface InviteCodeContext {
  code: string
  codeKind: 'invite' | 'entitlement'
  // Invite-specific
  inviteCodeId?: string
  codeType?: 'ssi_admin' | 'govt_admin' | 'school_admin' | 'school_admin_join' | 'teacher' | 'student' | 'tester'
  groupName?: string
  schoolName?: string
  className?: string
  courseName?: string
  // Entitlement-specific
  entitlementCodeId?: string
  label?: string
  accessType?: 'full' | 'courses'
  grantedCourses?: readonly string[]
  accessDescription?: string
  durationDescription?: string
  // Set when validateCode ran with a bearer token and the signed-in user has
  // already redeemed this code's context — subsequent redeems of a personal
  // link go straight to their surface (founder ruling 2026-07-20).
  alreadyRedeemed?: boolean
  redirectTo?: string
  // Species 1 (founder-ruled 2026-07-20): this code is bound to a
  // PRE-PROVISIONED account — the link IS the login. Zero screens: no
  // capture, no confirm; the client mints the session and routes to
  // redirectTo directly.
  personal?: boolean
}

// Module-level singleton state
const pendingCode = ref<InviteCodeContext | null>(null)
const validationError = ref<string | null>(null)
// A throttle refusal is NOT a bad code, and must never be reported as one:
// a whole class opens the same join link from one school NAT, so the person
// looking at the screen is holding a perfectly good link (production,
// 2026-08-31). RedeemCode.vue reads this to say so.
const validationThrottled = ref(false)
// The code check gave up waiting (Fitzalan, 2026-10-15: a school leader's
// redeem link sat on "Checking your code" for good, and /api/code/validate
// never reached the server). A request that never settles is not a bad code
// either: RedeemCode.vue reads this to say the link is fine and offer a retry.
const validationTimedOut = ref(false)
const isValidating = ref(false)
const isRedeeming = ref(false)

// Single-flight guard for redeemCode(). RedeemCode.vue calls doRedeem() from
// two sites (handleVerifyOtp's direct call, and the isSignedIn watcher that
// fires when the OTP verify's auth-state-change event lands) — both are
// intentional (the watcher is the primary path; the direct call covers the
// watcher not firing fast enough), but without this guard both could reach
// here concurrently and POST /api/code/redeem twice before either had
// cleared pendingCode, doubling up server-side writes (confirmed: 3 `schools`
// rows for one admin on staging). Memoizing the in-flight promise means the
// second caller gets the first call's result instead of firing a second
// request.
let inFlightRedeem: Promise<{ success: boolean; role?: string; redirectTo?: string; label?: string; codeKind?: string; error?: string; throttled?: boolean }> | null = null

// Restore from sessionStorage on load (survives OAuth redirect)
const SESSION_KEY = 'ssi-pending-invite-code'

/** How long the code check may take, request and answer together, before we stop waiting. */
export const VALIDATE_TIMEOUT_MS = 15_000
try {
  const stored = sessionStorage.getItem(SESSION_KEY)
  if (stored) {
    pendingCode.value = JSON.parse(stored)
  }
} catch {}

function persistPendingCode() {
  try {
    if (pendingCode.value) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(pendingCode.value))
    } else {
      sessionStorage.removeItem(SESSION_KEY)
    }
  } catch {
    // Blocked or full storage must not reject a valid code; keep it in memory.
  }
}

// Validation state is shared across callers; only the latest check may publish.
let validationVersion = 0

export function useInviteCode() {
  async function validateCode(code: string, authToken?: string): Promise<boolean> {
    const version = ++validationVersion
    validationError.value = null
    validationThrottled.value = false
    validationTimedOut.value = false
    isValidating.value = true
    // One deadline over the request AND its body. Aborting cancels the real
    // request; the race makes the deadline hold even where fetch has been
    // wrapped by something that ignores the signal (a filter's injected script).
    const controller = new AbortController()
    const gaveUp = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(new Error('timed out')))
    })
    gaveUp.catch(() => {})
    const deadline = setTimeout(() => controller.abort(), VALIDATE_TIMEOUT_MS)
    try {
      const res = await Promise.race([gaveUp, fetch('/api/code/validate', {
        signal: controller.signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Optional bearer: lets the server report alreadyRedeemed for the
          // signed-in user so a re-clicked personal link goes straight to
          // their surface instead of re-running the flow.
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
      })])
      const data = await Promise.race([gaveUp, res.json()])
      if (version !== validationVersion) return false
      if (data.valid) {
        if (data.codeKind === 'entitlement') {
          pendingCode.value = {
            code: code.trim().toUpperCase(),
            codeKind: 'entitlement',
            entitlementCodeId: data.entitlementCodeId,
            label: data.label,
            accessType: data.accessType,
            grantedCourses: data.grantedCourses,
            accessDescription: data.accessDescription,
            durationDescription: data.durationDescription,
          }
        } else {
          pendingCode.value = {
            code: code.trim().toUpperCase(),
            codeKind: 'invite',
            inviteCodeId: data.inviteCodeId,
            codeType: data.codeType,
            groupName: data.context?.groupName,
            schoolName: data.context?.schoolName,
            className: data.context?.className,
            courseName: data.context?.courseName,
            alreadyRedeemed: data.alreadyRedeemed === true,
            redirectTo: data.redirectTo,
            personal: data.personal === true,
          }
        }
        persistPendingCode()
        return true
      } else {
        if (res.status === 429) validationThrottled.value = true
        validationError.value = data.error || 'Invalid code'
        return false
      }
    } catch (err: any) {
      if (version !== validationVersion) return false
      if (controller.signal.aborted) {
        validationTimedOut.value = true
        validationError.value = "We couldn't reach our server to check your code."
        return false
      }
      validationError.value = err?.message || 'Failed to validate code'
      return false
    } finally {
      clearTimeout(deadline)
      if (version === validationVersion) isValidating.value = false
    }
  }

  async function redeemCode(authToken: string): Promise<{ success: boolean; role?: string; redirectTo?: string; label?: string; codeKind?: string; courseCode?: string | null; error?: string; throttled?: boolean }> {
    // Single-flight: a redemption already in progress wins; concurrent callers
    // (see comment on inFlightRedeem above) get its result rather than firing
    // a second request. Checked+set synchronously (no await before it), so
    // there's no interleaving window between the check and the set.
    if (inFlightRedeem) return inFlightRedeem
    if (!pendingCode.value) {
      return { success: false, error: 'No pending code' }
    }
    isRedeeming.value = true
    const codeAtStart = pendingCode.value
    inFlightRedeem = (async () => {
      try {
        const res = await fetch('/api/code/redeem', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            code: codeAtStart.code,
            codeKind: codeAtStart.codeKind,
          }),
        })
        const data = await res.json()
        if (data.success) {
          // A newer link may have been validated while this redemption waited.
          if (pendingCode.value === codeAtStart) clearStoredCode()
          return {
            success: true,
            codeKind: codeAtStart.codeKind,
            role: data.role,
            label: data.label,
            redirectTo: data.redirectTo,
            courseCode: data.courseCode,
          }
        } else {
          return { success: false, throttled: res.status === 429, error: data.error || 'Failed to redeem code' }
        }
      } catch (err: any) {
        return { success: false, error: err?.message || 'Failed to redeem code' }
      } finally {
        isRedeeming.value = false
      }
    })()
    try {
      return await inFlightRedeem
    } finally {
      // Clear after assignment even if a fetch wrapper throws synchronously.
      inFlightRedeem = null
    }
  }

  /**
   * Possession-based onboarding (archive/docs-retired-2026-08-24/schools/email-deliverability-plan.md,
   * Option A) — no OTP round-trip. Creates the account + mints a session
   * server-side from possession of the (already-validated) pending invite
   * code alone. Caller still owns calling supabase.auth.setSession(...) with
   * the returned tokens and then redeemCode() exactly as the OTP path does.
   */
  async function possessionRedeem(
    email: string,
    displayName?: string
  ): Promise<{ success: boolean; session?: { access_token: string; refresh_token: string }; reason?: string; error?: string; throttled?: boolean }> {
    if (!pendingCode.value || pendingCode.value.codeKind !== 'invite') {
      return { success: false, error: 'No pending invite code' }
    }
    try {
      const res = await fetch('/api/auth/possession-redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: pendingCode.value.code,
          email,
          displayName,
        }),
      })
      const data = await res.json()
      if (data.success) {
        return { success: true, session: data.session }
      }
      return { success: false, reason: data.reason, throttled: res.status === 429, error: data.error || 'Failed to set up your account' }
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to set up your account' }
    }
  }

  /**
   * Straight-in link redemption — the invite LINK is the credential. No email
   * typed: the server mints the account + a session from possession of the
   * (already-validated) pending code alone (api/auth/possession-redeem.ts
   * linkAuth mode), flagging it for a real-email prompt on first run. Same
   * return shape as possessionRedeem; caller owns setSession() + redeemCode().
   */
  async function linkPossessionRedeem(
    displayName?: string
  ): Promise<{ success: boolean; session?: { access_token: string; refresh_token: string }; reason?: string; error?: string; throttled?: boolean }> {
    if (!pendingCode.value || pendingCode.value.codeKind !== 'invite') {
      return { success: false, error: 'No pending invite code' }
    }
    try {
      const res = await fetch('/api/auth/possession-redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: pendingCode.value.code,
          linkAuth: true,
          displayName,
        }),
      })
      const data = await res.json()
      if (data.success) {
        return { success: true, session: data.session }
      }
      return { success: false, reason: data.reason, throttled: res.status === 429, error: data.error || 'Failed to sign you in' }
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to sign you in' }
    }
  }

  function clearPendingCode() {
    // A discarded check must not restore its code or error when it finishes.
    validationVersion++
    isValidating.value = false
    clearStoredCode()
  }

  function clearStoredCode() {
    pendingCode.value = null
    validationError.value = null
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      // Blocked storage must not turn a successful redemption into a failure.
    }
  }

  return {
    pendingCode: readonly(pendingCode),
    validationError: readonly(validationError),
    validationThrottled: readonly(validationThrottled),
    validationTimedOut: readonly(validationTimedOut),
    isValidating: readonly(isValidating),
    isRedeeming: readonly(isRedeeming),
    validateCode,
    redeemCode,
    possessionRedeem,
    linkPossessionRedeem,
    clearPendingCode,
  }
}
